import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardCheck, ShoppingBag, Wallet, Boxes, CalendarDays, FileText,
} from "lucide-react";
import { toast } from "sonner";

/** Phase 6 — Promoter "My Reports" aggregate page.
 *
 * Shows ONLY the logged-in promoter's records (server endpoints already scope
 * to /me). No filters, no exports of other promoters' data, no admin links.
 * Acts as a read-only digest with deep links into the existing pages.
 */
export default function PromoterMyReports() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/promoter/calc/me").catch(() => ({ data: null })),
      api.get("/promoter/sales").catch(() => ({ data: [] })),
      api.get("/promoter/stock-reports").catch(() => ({ data: [] })),
      api.get("/promoter/attendance/me").catch(() => ({ data: [] })),
      api.get("/promoter/attendance/leave-requests").catch(() => ({ data: [] })),
      api.get("/promoter/terminations/me").catch(() => ({ data: [] })),
    ])
      .then(([calc, sales, stock, attendance, leaves, terminations]) => {
        if (!mounted) return;
        // Compute attendance summary from the rows (the /me endpoint returns
        // {month, rows[]} or a flat list).
        const attRows = Array.isArray(attendance.data) ? attendance.data : (attendance.data?.rows || []);
        const summary = attRows.length === 0 ? null : {
          present: attRows.filter((a) => a.status === "present" || a.status === "checked_in").length,
          absent: attRows.filter((a) => a.status === "absent").length,
          late: attRows.filter((a) => a.is_late).length,
          half_day: attRows.filter((a) => a.status === "half_day").length,
        };
        setData({
          calc: calc.data,
          sales: sales.data || [],
          stock: stock.data || [],
          attendance: summary,
          leaves: Array.isArray(leaves.data) ? leaves.data : (leaves.data?.rows || []),
          terminations: terminations.data || [],
        });
      })
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const month = new Date().toISOString().slice(0, 7);
  const monthSales = (data.sales || []).filter((s) => (s.sale_date || "").startsWith(month));
  const approvedThisMonth = monthSales.filter((s) => s.status === "approved");
  const pendingThisMonth = monthSales.filter((s) => s.status === "pending_verification");
  const recentStock = (data.stock || []).slice(0, 5);

  return (
    <div className="space-y-4 pb-6" data-testid="my-reports-page">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Reports</h1>
        <p className="text-xs text-slate-500 mt-0.5">All your own records — no other promoter data.</p>
      </header>

      {loading && <Card><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="my-reports-loading">Loading…</CardContent></Card>}

      {!loading && (
        <>
          <Tile to="/promoter/my-incentive" icon={Wallet} title="Earnings (this month)" testId="my-reports-earnings"
                primary={`NPR ${Number(data.calc?.payable_incentive || 0).toLocaleString()}`}
                sub={`Achievement ${Number(data.calc?.achievement_pct || 0).toFixed(1)}% · Net sales NPR ${Number(data.calc?.approved_sales_value || 0).toLocaleString()}`} />

          <Tile to="/promoter/my-sales" icon={ShoppingBag} title="Sales (this month)" testId="my-reports-sales"
                primary={`${approvedThisMonth.length} approved · ${pendingThisMonth.length} pending`}
                sub={`Approved net NPR ${approvedThisMonth.reduce((a, s) => a + Number(s.net_sales_amount || 0), 0).toLocaleString()}`} />

          <Tile to="/promoter/my-attendance" icon={ClipboardCheck} title="Attendance" testId="my-reports-attendance"
                primary={data.attendance ? `${data.attendance.present || 0} present · ${data.attendance.absent || 0} absent` : "—"}
                sub={data.attendance ? `Late: ${data.attendance.late || 0} · Half-day: ${data.attendance.half_day || 0}` : "Open to see your attendance"} />

          <Tile to="/promoter/stock" icon={Boxes} title="Stock & Display" testId="my-reports-stock"
                primary={`${data.stock?.length || 0} submitted`}
                sub={recentStock.length ? `Latest: ${recentStock[0].brand} ${recentStock[0].product_model} (${recentStock[0].display_condition})` : "Submit your first report from the Stock tab"} />

          <Tile to="/promoter/leave" icon={CalendarDays} title="Leave Requests" testId="my-reports-leave"
                primary={`${data.leaves?.length || 0} request(s)`}
                sub={leaveSummary(data.leaves)} />

          {data.terminations?.length ? (
            <Card data-testid="my-reports-termination">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={14} className="text-rose-600" />
                  <span className="text-[11px] uppercase font-semibold tracking-wide text-rose-700">
                    Termination / Clearance
                  </span>
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {data.terminations[0].termination_type?.replace(/_/g, " ")}
                </div>
                <div className="text-xs text-slate-600">
                  Status: <Badge className="bg-slate-100 text-slate-700">{(data.terminations[0].status || "").replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Proposed LWD: {data.terminations[0].proposed_last_working_date || "—"}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <div className="text-[11px] text-slate-400 text-center pt-2" data-testid="my-reports-footnote">
            All data here is your own. To export, contact your Product Manager.
          </div>
        </>
      )}
    </div>
  );
}

function leaveSummary(leaves) {
  if (!leaves || leaves.length === 0) return "No requests submitted";
  const pending = leaves.filter((l) => l.status === "pending").length;
  const approved = leaves.filter((l) => l.status === "approved").length;
  return `${approved} approved · ${pending} pending`;
}

function Tile({ to, icon: Icon, title, primary, sub, testId }) {
  return (
    <Link to={to} className="block" data-testid={testId}>
      <Card className="hover:shadow-sm transition-shadow">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={14} className="text-slate-500" />
            <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">{title}</span>
          </div>
          <div className="text-base font-bold text-slate-900">{primary}</div>
          {sub ? <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div> : null}
        </CardContent>
      </Card>
    </Link>
  );
}
